/**
 * Precision-aware date primitive for the GET-910 flexible-date migration.
 *
 * Operates on the (value, precision) pair carried by the GraphQL `FlexibleDate`
 * type. Takes the two fields as plain arguments rather than a model, because
 * codegen emits several `FlexibleDate` shapes (API.ts, models/, DataStore).
 *
 * Supersedes `DateISO` — that class throws on anything that isn't a full
 * `YYYY-MM-DD`, which is exactly what a partial date is.
 *
 * Mirrors `services/common/partial_date.py`. One deliberate divergence: `bounds`
 * returns ISO strings rather than `Date` objects. ISO dates compare
 * lexicographically, so ordering works without ever constructing a `Date` and
 * inheriting its timezone hazards.
 *
 * See `specs/flexible-partial-dates-migration.md` §Part 1.
 */

import { DatePrecision, FlexibleDateInput } from "../api/API";

export const DISCLAIMER = "(exact date not recalled)";
export const DISCLAIMER_PLURAL = "(exact dates not recalled)";

/** Rendered when the field itself is absent — nothing was entered. */
export const ABSENT = "—";

/** Callers hand us precision as an enum member or a raw string off the wire. */
export type Precision = DatePrecision | string | null | undefined;

const DATED: readonly DatePrecision[] = [
  DatePrecision.YEAR,
  DatePrecision.MONTH,
  DatePrecision.DAY,
];
const DATED_OR_PRESENT: readonly DatePrecision[] = [
  ...DATED,
  DatePrecision.PRESENT,
];

/**
 * PRESENT is legal only on the CT injury *end*. Keys are qualified by model
 * because the CT convention flips: `dateOfInjury` is the end on InjuryInfo but the
 * start on AssessmentInjury (spec §CT convention), so a bare field name is
 * ambiguous. Employment never allows PRESENT — `stillWorkingHere` encodes that.
 */
export const FIELD_PRECISIONS: Readonly<
  Record<string, readonly DatePrecision[]>
> = {
  "EmploymentRecord.dateOfHire": DATED,
  "EmploymentRecord.dateOfTermination": DATED,
  "InjuryInfo.startDateOfInjury": DATED,
  "InjuryInfo.dateOfInjury": DATED_OR_PRESENT, // CT end
  "AssessmentInjury.dateOfInjury": DATED, // flipped — CT start
  "AssessmentInjury.dateOfInjuryEnd": DATED_OR_PRESENT, // CT end
};

const PARTS_FOR: Readonly<Record<string, number>> = {
  [DatePrecision.YEAR]: 1,
  [DatePrecision.MONTH]: 2,
  [DatePrecision.DAY]: 3,
};
const WIDTHS = [4, 2, 2];

/** Normalize a raw string or enum member to a member; null if unrecognized. */
export function coercePrecision(precision: Precision): DatePrecision | null {
  if (precision === null || precision === undefined) return null;
  const match = DATED_OR_PRESENT.find((p) => p === precision);
  return match ?? null;
}

/** Days in a 1-indexed month. Day 0 of the next month is the last of this one. */
function daysInMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

/**
 * Split a value into [year, month, day] at its precision, or null if malformed.
 * Enforces zero-padded fixed-width parts, so `2019-5-14` is rejected rather than
 * silently normalized.
 */
function parse(
  value: string,
  precision: DatePrecision,
): [number, number, number] | null {
  const expected = PARTS_FOR[precision];
  if (expected === undefined) return null;

  const parts = value.split("-");
  if (parts.length !== expected) return null;
  if (parts.some((p, i) => p.length !== WIDTHS[i] || !/^\d+$/.test(p)))
    return null;

  const [year, month = 1, day = 1] = parts.map(Number);
  return [year, month, day];
}

/** True when the triple is a real calendar date (rejects 2019-13, 2019-02-30). */
function isRealDate(year: number, month: number, day: number): boolean {
  if (month < 1 || month > 12) return false;
  return day >= 1 && day <= daysInMonth(year, month);
}

const pad = (n: number, w = 2): string => String(n).padStart(w, "0");
const iso = (year: number, month: number, day: number): string =>
  `${pad(year, 4)}-${pad(month)}-${pad(day)}`;

/** The grammar each precision requires, for validation messages. */
export const DATE_GRAMMAR: Readonly<Record<DatePrecision, string>> = {
  [DatePrecision.YEAR]: "YYYY",
  [DatePrecision.MONTH]: "YYYY-MM",
  [DatePrecision.DAY]: "YYYY-MM-DD",
  [DatePrecision.PRESENT]: "null",
};

/**
 * The value matches its precision's grammar and names a real calendar date.
 *
 * YEAR needs `YYYY`, MONTH `YYYY-MM`, DAY `YYYY-MM-DD`; PRESENT carries no value.
 * Field-agnostic — the per-field PRESENT allow-list lives in `isValid`. The
 * Python twin backs the `FlexibleDate` pydantic model's construction-time check.
 */
export function isCoherent(
  value: string | null,
  precision: Precision,
): boolean {
  const resolved = coercePrecision(precision);
  if (resolved === null) return false;
  if (resolved === DatePrecision.PRESENT) return value === null;
  if (value === null) return false;

  const parsed = parse(value, resolved);
  return parsed !== null && isRealDate(...parsed);
}

/**
 * Why the pair is incoherent, or null when it's fine.
 *
 * Same structure as the `ValueError` raised by the `FlexibleDate` pydantic model
 * in `services/models/flexible_date.py` — same fields, same order — so the two
 * rejections are recognisably the same failure. Not byte-identical: quoting
 * follows each language (`repr` gives `'2019-05'` / `None`, `JSON.stringify`
 * gives `"2019-05"` / `null`). Nothing compares them programmatically, and this
 * string surfaces as a Yup form error, so it stays JS-idiomatic.
 */
export function coherenceError(
  value: string | null,
  precision: Precision,
): string | null {
  const resolved = coercePrecision(precision);
  if (resolved === null)
    return `unknown precision ${JSON.stringify(precision)}`;
  if (isCoherent(value, resolved)) return null;
  return `precision=${resolved} requires value ${JSON.stringify(
    DATE_GRAMMAR[resolved],
  )}, got ${JSON.stringify(value)}`;
}

/**
 * Build a write-ready `FlexibleDateInput`, or null when the pair is incoherent.
 *
 * The TS counterpart to constructing the `FlexibleDate` pydantic model: callers
 * end up holding either a valid value or nothing, so an incoherent pair can't be
 * handed to a mutation. Returns the generated input type rather than a local
 * shape so it drops straight into a GraphQL variable.
 */
export function makeFlexibleDate(
  value: string | null,
  precision: Precision,
): FlexibleDateInput | null {
  const resolved = coercePrecision(precision);
  if (resolved === null || !isCoherent(value, resolved)) return null;
  return { value, precision: resolved };
}

/**
 * `isCoherent` plus the per-field precision allow-list.
 *
 * `field` is qualified — `"EmploymentRecord.dateOfHire"`. An unknown field is
 * rejected rather than waved through, so a typo fails loudly at the write site.
 */
export function isValid(
  field: string,
  value: string | null,
  precision: Precision,
): boolean {
  const resolved = coercePrecision(precision);
  const allowed = FIELD_PRECISIONS[field];
  if (
    resolved === null ||
    allowed === undefined ||
    !allowed.includes(resolved)
  ) {
    return false;
  }
  return isCoherent(value, resolved);
}

/**
 * The [earliest, latest] real days a value spans at its precision, as ISO strings.
 * `"2019"` spans 2019-01-01…2019-12-31. Null for PRESENT (unbounded), absent, or
 * malformed values.
 */
export function bounds(
  value: string | null,
  precision: Precision,
): { lo: string; hi: string } | null {
  const resolved = coercePrecision(precision);
  if (
    value === null ||
    resolved === null ||
    resolved === DatePrecision.PRESENT
  ) {
    return null;
  }
  const parsed = parse(value, resolved);
  if (parsed === null) return null;

  const [year, month, day] = parsed;
  if (!isRealDate(year, month, day)) return null;

  if (resolved === DatePrecision.DAY) {
    return { lo: iso(year, month, day), hi: iso(year, month, day) };
  }
  if (resolved === DatePrecision.MONTH) {
    return {
      lo: iso(year, month, 1),
      hi: iso(year, month, daysInMonth(year, month)),
    };
  }
  return { lo: iso(year, 1, 1), hi: iso(year, 12, 31) };
}

/**
 * Precision-aware ordering: -1 if a < b, 1 if a > b, 0 if they overlap.
 *
 * Compares by *bounds*, so a coarse value containing the other is neither less nor
 * greater — it returns 0. That is what makes `compare(...) <= 0` the right test for
 * "start <= end": a year-only start does not false-flag against a specific end day
 * inside it. PRESENT sorts after everything (+inf); an unresolvable side returns 0
 * so garbage never hard-fails a range check.
 */
export function compare(
  aValue: string | null,
  aPrecision: Precision,
  bValue: string | null,
  bPrecision: Precision,
): number {
  const aPresent = coercePrecision(aPrecision) === DatePrecision.PRESENT;
  const bPresent = coercePrecision(bPrecision) === DatePrecision.PRESENT;
  if (aPresent || bPresent) {
    if (aPresent && bPresent) return 0;
    return aPresent ? 1 : -1;
  }

  const a = bounds(aValue, aPrecision);
  const b = bounds(bValue, bPrecision);
  if (a === null || b === null) return 0;
  if (a.hi < b.lo) return -1;
  if (a.lo > b.hi) return 1;
  return 0;
}

/**
 * Read helper for the migration window: the flexible field, else the legacy date.
 *
 * Wraps a legacy `AWSDate` as `[value, DAY]` so readers work on rows that predate
 * the backfill (spec Lifecycle Stage 3). Every migrated reader goes through this —
 * reading the legacy column raw is what silently drops precision.
 */
export function coalesce(
  flexibleValue: string | null,
  flexiblePrecision: Precision,
  legacy: string | null,
): [string | null, DatePrecision | null] {
  const resolved = coercePrecision(flexiblePrecision);
  if (resolved !== null) return [flexibleValue, resolved];
  if (legacy !== null) return [legacy, DatePrecision.DAY];
  return [null, null];
}

/**
 * The precision a bare value's grammar implies (`YYYY` / `YYYY-MM` / `YYYY-MM-DD`),
 * or null when it names no real date.
 *
 * Only for stores that keep a date at its precision with no precision field — the
 * HOI insights' `InjuryCausationInfo` dates. Anywhere a `FlexibleDate` exists, read
 * its `precision` instead: a value never stands in for one.
 */
export function precisionFromValue(value: string): DatePrecision | null {
  const dated = [DatePrecision.DAY, DatePrecision.MONTH, DatePrecision.YEAR];
  return dated.find((p) => isCoherent(value, p)) ?? null;
}

/**
 * The legacy `AWSDate` a flexible date can fill: the value at DAY precision, null
 * at every coarser one.
 *
 * The dual-write rule for the migration window (spec Lifecycle Stage 2): a coarse
 * value has no honest `YYYY-MM-DD`, and null leaves an un-migrated reader blank
 * rather than showing a fabricated `01/01`.
 */
export function toDayIso(
  value: string | null,
  precision: Precision,
): string | null {
  if (coercePrecision(precision) !== DatePrecision.DAY) return null;
  return isCoherent(value, DatePrecision.DAY) ? value : null;
}

/**
 * The `FlexibleDate` column a form carrier can fill: the pair when it names a
 * real date, null when it doesn't.
 *
 * The companion to `toDayIso` — together they are the dual-write rule. A form
 * carries a precision the user picked before entering a value ("Month", still
 * blank) so the control survives a remount; that is real UI state but not a
 * storable date, and writing it would put an incoherent `FlexibleDate` in the row.
 */
export function toStoredPair(
  pair: FlexibleDateInput | null | undefined,
): FlexibleDateInput | null {
  if (!pair) return null;
  return isCoherent(pair.value ?? null, pair.precision) ? pair : null;
}

/** Render without the disclaimer — for ranges, which carry one shared suffix. */
export function formatBare(value: string | null, precision: Precision): string {
  const resolved = coercePrecision(precision);
  if (resolved === DatePrecision.PRESENT) return "present";
  if (value === null) return ABSENT;
  if (resolved === null) return value; // unknown precision — show it, don't invent one

  const parsed = parse(value, resolved);
  if (parsed === null) return value; // malformed renders visibly, not as a wrong day

  const [year, month, day] = parsed;
  if (resolved === DatePrecision.DAY)
    return `${pad(month)}/${pad(day)}/${year}`;
  if (resolved === DatePrecision.MONTH) return `${pad(month)}/${year}`;
  return String(year);
}

/**
 * Disclaimer suffix for a set of bounds; plural when more than one is imprecise.
 * Empty string when nothing is imprecise, so callers can append unconditionally.
 */
export function disclaimerFor(...precisions: Precision[]): string {
  const imprecise = precisions.filter((p) => {
    const resolved = coercePrecision(p);
    return resolved === DatePrecision.YEAR || resolved === DatePrecision.MONTH;
  }).length;

  if (imprecise === 0) return "";
  return imprecise === 1 ? DISCLAIMER : DISCLAIMER_PLURAL;
}

/**
 * The preposition before a single date: "in 2019", "in 05/2019", "on 05/14/2019".
 * Anything that isn't YEAR/MONTH — DAY, a legacy exact date — takes "on".
 */
export function datePreposition(precision: Precision): "in" | "on" {
  const resolved = coercePrecision(precision);
  return resolved === DatePrecision.YEAR || resolved === DatePrecision.MONTH
    ? "in"
    : "on";
}

/**
 * Render a single date, appending the disclaimer when precision is YEAR/MONTH.
 * DAY and PRESENT get none.
 */
export function formatUs(value: string | null, precision: Precision): string {
  // An absent field has nothing to be imprecise about — "— (exact date not
  // recalled)" reads as a recall failure rather than a blank.
  // `isValid` already rejects dated-precision-with-null-value, so this only
  // guards unvalidated callers.
  if (value === null && coercePrecision(precision) !== DatePrecision.PRESENT)
    return ABSENT;

  const rendered = formatBare(value, precision);
  const suffix = disclaimerFor(precision);
  return suffix ? `${rendered} ${suffix}` : rendered;
}
